import React, { useEffect, useState } from "react";
import { Container, Card, Form, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { useAuth } from "../auth/AuthContext";

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

export default function InchideLuna() {
  const { user } = useAuth();
  const [firma, setFirma] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [error, setError] = useState("");

  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const months = [
    { value: 1, label: "Ianuarie" },
    { value: 2, label: "Februarie" },
    { value: 3, label: "Martie" },
    { value: 4, label: "Aprilie" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Iunie" },
    { value: 7, label: "Iulie" },
    { value: 8, label: "August" },
    { value: 9, label: "Septembrie" },
    { value: 10, label: "Octombrie" },
    { value: 11, label: "Noiembrie" },
    { value: 12, label: "Decembrie" },
  ];

  const years = [];
  for (let y = currentYear - 5; y <= currentYear + 5; y++) {
    years.push(y);
  }

  const fetchFirma = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await authFetch("/firma/1");
      if (data) {
        setFirma(data);
      }
    } catch (e) {
      setError(e.message || "Eroare la încărcarea datelor firmei.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirma();
  }, []);

  const handleCloseMonth = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      const updatedFirma = await authFetch("/firma/close-month", {
        method: "POST",
        body: JSON.stringify({
          year: Number(selectedYear),
          month: Number(selectedMonth),
        }),
      });
      if (updatedFirma) {
        setFirma(updatedFirma);
        toast.success(`Luna a fost închisă cu succes până la data de ${formatDateRo(updatedFirma.bxlast_date)}.`);
      }
    } catch (e) {
      toast.error(e.message || "Eroare la închiderea lunii.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleReopenMonth = async () => {
    if (!window.confirm("Sigur doriți să redeschideți perioada? Toate documentele vor putea fi editate fără restricții de dată.")) {
      return;
    }
    setReopenLoading(true);
    try {
      const updatedFirma = await authFetch("/firma/reopen-month", {
        method: "POST",
      });
      if (updatedFirma) {
        setFirma(updatedFirma);
        toast.success("Perioada a fost redeschisă cu succes.");
      }
    } catch (e) {
      toast.error(e.message || "Eroare la redeschiderea perioadei.");
    } finally {
      setReopenLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-lg border-0">
            <Card.Header className="bg-primary text-white py-3">
              <h4 className="mb-0 text-center fw-bold">Închide Luna</h4>
            </Card.Header>
            <Card.Body className="p-4">
              {error && <Alert variant="danger">{error}</Alert>}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Se încarcă setările firmei...</p>
                </div>
              ) : (
                <>
                  {/* Stare curentă */}
                  <div className="mb-4 text-center p-3 bg-light rounded border">
                    <h5 className="text-muted mb-2">Perioadă curentă blocată</h5>
                    {firma?.bxlast_date ? (
                      <h3 className="text-danger fw-bold mb-0">
                        {formatDateRo(firma.bxlast_date)}
                      </h3>
                    ) : (
                      <h4 className="text-success fw-bold mb-0">
                        Toate perioadele sunt deschise
                      </h4>
                    )}
                    <small className="text-muted mt-2 d-block">
                      Orice document cu data mai mică sau egală cu această dată nu va putea fi adăugat, modificat sau șters.
                    </small>
                  </div>

                  {/* Formular închidere lună */}
                  <Form onSubmit={handleCloseMonth} className="mb-4">
                    <h5 className="fw-bold mb-3">Închide o perioadă nouă</h5>
                    <Row className="g-3 align-items-end">
                      <Col sm={6}>
                        <Form.Group controlId="selectMonth">
                          <Form.Label className="fw-semibold">Luna</Form.Label>
                          <Form.Select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(Number(e.target.value))}
                          >
                            {months.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="selectYear">
                          <Form.Label className="fw-semibold">Anul</Form.Label>
                          <Form.Select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                          >
                            {years.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-grid gap-2 mt-4">
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={submitLoading}
                        className="fw-bold py-2"
                      >
                        {submitLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Se închide luna...
                          </>
                        ) : (
                          "Închide luna"
                        )}
                      </Button>
                    </div>
                  </Form>

                  {/* Redeschidere perioadă (doar admin/standard cu confirmare) */}
                  <div className="border-top pt-4 text-center">
                    <h5 className="fw-bold text-warning mb-3">Opțiuni avansate</h5>
                    <Button
                      variant="outline-danger"
                      onClick={handleReopenMonth}
                      disabled={reopenLoading || !firma?.bxlast_date}
                      className="px-4 py-2 fw-semibold"
                    >
                      {reopenLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Se redeschide...
                        </>
                      ) : (
                        "Redeschide perioada"
                      )}
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

